import { useEffect, useState } from 'react';
import { AlertCircle, Image as ImageIcon, Loader2 } from 'lucide-react';
import { driveThumbnailUrl } from '../lib/drive';

/**
 * Live check that an image link actually loads, with a small preview box.
 * Catches the two Drive mistakes people keep making: pasting a FOLDER link
 * instead of the image's own link, and pasting a file that isn't shared as
 * "Anyone with the link" (Drive's thumbnail endpoint then 403s and the
 * <img> fires onError). The parent gets {ok, message} and can block saving
 * until ok is true.
 */

export interface ImageCheckResult {
  ok: boolean;
  message: string | null;
}

function looksLikeUrl(value: string): boolean {
  return /^https?:\/\/\S+$/i.test(value.trim());
}

function isDriveFolderLink(url: string): boolean {
  return /drive\.google\.com\/(?:drive\/)?(?:u\/\d+\/)?folders\//.test(url);
}

export default function ImageLinkCheck({
  url,
  alt,
  onResult,
}: {
  url: string;
  alt: string;
  /** Called whenever the check's outcome changes. */
  onResult: (result: ImageCheckResult) => void;
}) {
  const trimmed = url.trim();

  // Debounce so we don't fire a request per keystroke while a link is pasted
  // or edited by hand.
  const [debounced, setDebounced] = useState(trimmed);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(trimmed), 400);
    return () => clearTimeout(t);
  }, [trimmed]);

  const [imgState, setImgState] = useState<'loading' | 'ok' | 'error'>('loading');
  useEffect(() => setImgState('loading'), [debounced]);

  let status: 'empty' | 'checking' | 'ok' | 'error';
  let message: string | null = null;

  if (!debounced) {
    status = 'empty';
  } else if (isDriveFolderLink(debounced)) {
    status = 'error';
    message =
      'This is a Drive FOLDER link, not an image. Open the folder, right-click the image itself → Share → copy that link.';
  } else if (!looksLikeUrl(debounced)) {
    status = 'error';
    message = 'Not a valid link — it must start with http(s)://';
  } else if (imgState === 'loading') {
    status = 'checking';
  } else if (imgState === 'ok') {
    status = 'ok';
  } else {
    status = 'error';
    message = debounced.includes('drive.google.com')
      ? 'The image doesn’t load. On Drive, set the file’s sharing to "Anyone with the link" and make sure the link points at the image file itself.'
      : 'The image doesn’t load from this link. Check that it points directly at an image file and is publicly accessible.';
  }

  useEffect(() => {
    onResult({ ok: status === 'ok', message });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, message]);

  const showImg = status === 'checking' || status === 'ok';

  return (
    <div className="relative group/thumb shrink-0">
      <div
        className={`w-16 h-16 rounded-xl border flex items-center justify-center overflow-hidden ${
          status === 'error'
            ? 'border-red-300 bg-red-50'
            : status === 'ok'
              ? 'border-natural-border bg-stone-100'
              : 'border-dashed border-natural-border bg-stone-50'
        }`}
        title={
          status === 'error'
            ? message ?? 'Image failed to load'
            : status === 'ok'
              ? 'Image loads correctly'
              : 'Image preview'
        }
      >
        {status === 'empty' && <ImageIcon size={18} className="text-natural-text/25" />}
        {status === 'checking' && <Loader2 size={18} className="text-natural-text/40 animate-spin" />}
        {status === 'error' && <AlertCircle size={18} className="text-red-400" />}
        {showImg && (
          <img
            key={debounced}
            src={driveThumbnailUrl(debounced, 'w200')}
            alt={alt}
            referrerPolicy="no-referrer"
            onLoad={() => setImgState('ok')}
            onError={() => setImgState('error')}
            className={`w-full h-full object-cover ${status === 'ok' ? 'cursor-zoom-in' : 'hidden'}`}
          />
        )}
      </div>
      {status === 'ok' && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 hidden group-hover/thumb:block z-50 pointer-events-none">
          <div className="bg-white p-2.5 rounded-2xl border border-natural-border shadow-2xl w-[360px] max-w-[80vw]">
            <img
              src={driveThumbnailUrl(debounced, 'w1000')}
              alt={alt}
              referrerPolicy="no-referrer"
              className="w-full h-auto max-h-[480px] rounded-xl object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}
